import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import defaultClient from '@src/api/clients/default';
import { toast } from '@src/components/core/toaster';
import config from '@src/config';
import { useAuth } from '@src/context';
import useClientAutocomplete from '@src/hooks/useClientAutocomplete';
import paths from '@src/paths';

const ROLE_OPTIONS = [
  { value: 'viewer', label: 'Viewer (read-only dashboards)' },
  { value: 'editor', label: 'Editor (can update onboarding + assets)' },
  { value: 'admin', label: 'Admin (full access for this client)' },
];

const INITIAL_FORM = {
  email: '',
  firstName: '',
  lastName: '',
  role: ROLE_OPTIONS[0].value,
};

function ClientMemberInvitePage() {
  const { state } = useAuth();
  const user = state?.user;
  const isStaff = user?.is_staff || user?.is_superuser;

  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedParentCompany, setSelectedParentCompany] = useState(null);
  const [inviteScope, setInviteScope] = useState('client');
  const [form, setForm] = useState(INITIAL_FORM);
  const [sending, setSending] = useState(false);
  const [lastInvite, setLastInvite] = useState(null);
  const {
    options: clientOptions,
    loading: clientSearchLoading,
    handleInputChange: handleClientSearchInput,
  } = useClientAutocomplete();

  useEffect(() => {
    if (!isStaff) return;
    setClientsLoading(true);
    defaultClient
      .request({
        url: 'dashboard/clients/',
        method: 'GET',
        payload: {
          fields: 'id,name,parent_company,parent_company_name',
        },
      })
      .then((response) => {
        const list = Array.isArray(response?.data?.results)
          ? response.data.results
          : Array.isArray(response?.data)
            ? response.data
            : [];
        setClients(list);
      })
      .catch(() => {
        setClients([]);
        toast.error('Could not load clients. Please retry.');
      })
      .finally(() => setClientsLoading(false));
  }, [isStaff]);

  const sortedClients = useMemo(() => {
    return [...clientOptions].sort((a, b) => (a.label || '').localeCompare(b.label || ''));
  }, [clientOptions]);

  const parentCompanyOptions = useMemo(() => {
    const map = new Map();
    clients.forEach((client) => {
      if (client?.parent_company) {
        const key = String(client.parent_company);
        if (!map.has(key)) {
          map.set(key, {
            id: client.parent_company,
            name: client.parent_company_name || `Parent ${client.parent_company}`,
          });
        }
      }
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [clients]);

  const loginUrl = useMemo(() => {
    const origin = typeof window !== 'undefined' && window.location?.origin ? window.location.origin : '';
    return `${origin}${config.LOGIN_URL}`;
  }, []);

  const handleInputChange = useCallback((field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  }, []);

  const handleScopeChange = useCallback((event) => {
    const value = event.target.value;
    setInviteScope(value);
    setSelectedClient(null);
    setSelectedParentCompany(null);
    setLastInvite(null);
  }, []);

  const handleCopyLogin = useCallback(() => {
    const link = loginUrl || config.LOGIN_URL;
    if (!navigator?.clipboard || !link) {
      toast.error('Clipboard is not available in this browser.');
      return;
    }
    navigator.clipboard.writeText(link).then(
      () => toast.success('Login link copied'),
      () => toast.error('Unable to copy link')
    );
  }, [loginUrl]);

  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault();
      if (!isStaff) {
        toast.error('Only staff can invite new client members.');
        return;
      }
      if (inviteScope === 'client' && !selectedClient?.value) {
        toast.error('Select a client to invite this user to.');
        return;
      }
      if (inviteScope === 'parent' && !selectedParentCompany?.id) {
        toast.error('Select a parent company to invite this user to.');
        return;
      }
      if (!form.email.trim()) {
        toast.error('Email is required.');
        return;
      }

      setSending(true);
      const payload = {
        email: form.email.trim(),
        first_name: form.firstName.trim(),
        last_name: form.lastName.trim(),
        role: form.role,
      };
      if (inviteScope === 'client') {
        payload.client_id = selectedClient.value;
      } else {
        payload.parent_company_id = selectedParentCompany.id;
      }

      defaultClient
        .request({
          url: 'accounts/auth/client-memberships/invite/',
          method: 'POST',
          payload,
        })
        .then((response) => {
          setLastInvite({
            membership: response?.data || null,
            email: payload.email,
            clientName:
              inviteScope === 'client'
                ? selectedClient.label
                : selectedParentCompany.name,
            scope: inviteScope,
          });
          toast.success('Invite sent. We emailed them a link to join.');
          setForm(INITIAL_FORM);
        })
        .catch(() => {
          toast.error('Unable to send invite. Please try again.');
        })
        .finally(() => setSending(false));
    },
    [form, inviteScope, isStaff, selectedClient, selectedParentCompany]
  );

  if (!user) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
      </Box>
    );
  }

  if (!isStaff) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          This page is only available to staff users. Client members cannot invite other users.
        </Alert>
        <Button variant="contained" href={paths.dashboard.dailyPulse}>
          Back to dashboard
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={2} sx={{ mb: 3 }}>
        <Typography variant="h4">Invite client members</Typography>
        <Typography color="text.secondary">
          Create a user (or re-activate existing), add them to a client or parent company, and send them a password setup email that lands on the login screen.
        </Typography>
      </Stack>

      <Card component="form" onSubmit={handleSubmit}>
        <CardContent>
          <Stack spacing={3}>
            {clientsLoading && <LinearProgress />}
            <Stack spacing={1}>
              <Typography variant="subtitle2">Scope</Typography>
              <TextField
                label="Invite scope"
                value={inviteScope}
                onChange={handleScopeChange}
                select
                fullWidth
              >
                <MenuItem value="client">Single client</MenuItem>
                <MenuItem value="parent">Parent company</MenuItem>
              </TextField>
            </Stack>

            <Stack spacing={1}>
              <Typography variant="subtitle2">
                {inviteScope === 'client' ? 'Client' : 'Parent company'}
              </Typography>
              {inviteScope === 'client' ? (
                <Autocomplete
                  value={selectedClient}
                  onChange={(_, value) => setSelectedClient(value)}
                  onInputChange={handleClientSearchInput}
                  loading={clientSearchLoading}
                  options={sortedClients}
                  isOptionEqualToValue={(option, value) => option.value === value?.value}
                  getOptionLabel={(option) => option?.label || ''}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.value} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {option.label}
                      </Typography>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search clients"
                      placeholder="Start typing a client name"
                      InputProps={{
                        ...params.InputProps,
                              endAdornment: (
                                <>
                                  {clientSearchLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                      }}
                      required
                    />
                  )}
                  fullWidth
                />
              ) : (
                <Autocomplete
                  value={selectedParentCompany}
                  onChange={(_, value) => setSelectedParentCompany(value)}
                  options={parentCompanyOptions}
                  getOptionLabel={(option) => option?.name || ''}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search parent companies"
                      placeholder="Start typing a parent company"
                      required
                    />
                  )}
                  fullWidth
                />
              )}
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="First name"
                value={form.firstName}
                onChange={handleInputChange('firstName')}
                fullWidth
              />
              <TextField
                label="Last name"
                value={form.lastName}
                onChange={handleInputChange('lastName')}
                fullWidth
              />
            </Stack>

            <TextField
              label="Email"
              value={form.email}
              onChange={handleInputChange('email')}
              type="email"
              required
              fullWidth
            />

            <TextField
              label="Role"
              value={form.role}
              onChange={handleInputChange('role')}
              select
              helperText={
                inviteScope === 'client'
                  ? 'Roles apply only to this client.'
                  : 'Roles apply to the entire parent company.'
              }
              fullWidth
            >
              {ROLE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SendIcon />}
                disabled={sending}
              >
                {sending ? 'Sending invite…' : 'Send invite'}
              </Button>
              <Typography variant="body2" color="text.secondary">
                We&apos;ll create or update the user, attach them to the selected scope, and email them a password setup link.
              </Typography>
            </Stack>

            {lastInvite ? (
              <Box>
                <Divider sx={{ my: 2 }} />
                <Alert
                  severity="success"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      startIcon={<ContentCopyIcon fontSize="small" />}
                      onClick={handleCopyLogin}
                    >
                      Copy login link
                    </Button>
                  }
                >
                  Sent an invite to <strong>{lastInvite.email}</strong> for{' '}
                  <strong>{lastInvite.clientName}</strong>. They can log in at {loginUrl || config.LOGIN_URL} after setting a password.
                </Alert>
              </Box>
            ) : null}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}

ClientMemberInvitePage.displayName = 'ClientMemberInvitePage';

export default ClientMemberInvitePage;
